import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import useForm, { FieldValues } from 'hooks/useForm';
import TextInput from 'components/common/TextInput/TextInput';
import { isPercentage, validateRequiredField } from 'common/utils/validation.utils';
import InputSelect from 'components/common/InputSelect/InputSelect';
import NumberInput from 'components/common/NumberInput/NumberInput';
import { MultipleSelectOption } from 'components/common/MultipleSelect/MultipleSelect';
import MultipleSelect from 'components/common/MultipleSelect';
import Button from 'components/common/Button/Button';
import { removeNonDigits } from 'common/utils/string.utils';
import { RootStoreContext } from 'index';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { notify } from 'components/common/Toast/toast.helpers';

import styles from '../RegistrationProcess.module.scss';
import {
  BusinessMetricsFormInput,
  DefaultYesNoQuestion,
  PrimaryHomeImprovementCategory,
  PrequalificationStep,
} from '../registrationProcess.enums';
import {
  DEFAULT_YES_NO_QUESTION_OPTIONS,
  PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS,
} from '../registrationProcess.constants';
import {
  convertToDefaultQuestionValueFromBoolean,
  convertToBooleanFromDefaultQuestionValue,
  convertMonetaryToNumber,
} from '../registrationProcess.utils';
import { ANNUAL_REVENUE_AND_UNITS_SOLD_MAX_VALUE } from './registrationSteps.constants';

const BusinessMetrics = () => {
  const { prequalificationStore } = useContext(RootStoreContext);
  const {
    prequalificationInformation,
    updatePrequalificationInformation,
    setNextStepByCurrentStep,
    intermediaryApplicationId,
  } = prequalificationStore;

  const [isSpecifyHomeImprovementTypeNeeded, setIsSpecifyHomeImprovementTypeNeeded] = useState<boolean>(false);
  const [isHowManyInstallersDoYoHaveNeeded, setIsHowManyInstallersDoYoHaveNeeded] = useState<boolean>(false);
  const [isExplainSpecificLicenseNeeded, setIsExplainSpecificLicenseNeeded] = useState<boolean>(false);

  const defaultValues = {
    [BusinessMetricsFormInput.PrimaryHomeImprovementCategory]:
      prequalificationInformation?.primaryHomeImprovementCategory,
    [BusinessMetricsFormInput.OtherHomeImprovementCategory]:
      prequalificationInformation?.otherHomeImprovementCategory || [],
    [BusinessMetricsFormInput.DoYouInstallTheProductsYourself]: convertToDefaultQuestionValueFromBoolean(
      prequalificationInformation?.installTheProductsYourself,
    ),
    [BusinessMetricsFormInput.HoldAppropriateLicense]: convertToDefaultQuestionValueFromBoolean(
      prequalificationInformation?.holdAppropriateLicense,
    ),
    [BusinessMetricsFormInput.HowManyInstallersDoYouHave]: prequalificationInformation?.installersAmount,
    [BusinessMetricsFormInput.AnnualJobs]: prequalificationInformation?.annualJobsUnitsSold,
    [BusinessMetricsFormInput.PercentOfSalesFinanced]: prequalificationInformation?.percentOfSalesFinanced
      ? prequalificationInformation?.percentOfSalesFinanced * 100
      : '',
    [BusinessMetricsFormInput.AnnualRevenue]: prequalificationInformation?.annualRevenue,
    [BusinessMetricsFormInput.HowDidYouHearAboutHfs]: prequalificationInformation?.howDidYouHearAboutUs,
    [BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType]:
      prequalificationInformation?.specifyHomeImprovementType,
  };

  const { errors, values, setValue, validateField, handleSubmit, resetForm } = useForm<FieldValues>({
    defaultValues: defaultValues,
    handleValidation: (fieldName, value) => {
      if (!value) {
        switch (true) {
          case fieldName === BusinessMetricsFormInput.PrimaryHomeImprovementCategory: {
            return validateRequiredField(value, fieldName);
          }
          case fieldName === BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType &&
            !!isSpecifyHomeImprovementTypeNeeded: {
            return validateRequiredField(value);
          }
          case fieldName === BusinessMetricsFormInput.HowManyInstallersDoYouHave && isHowManyInstallersDoYoHaveNeeded: {
            return validateRequiredField(value);
          }
          case fieldName === BusinessMetricsFormInput.ExplainSpecificLicense && isExplainSpecificLicenseNeeded: {
            return validateRequiredField(value);
          }
        }
      } else {
        switch (fieldName) {
          case BusinessMetricsFormInput.PercentOfSalesFinanced: {
            return isPercentage(value, fieldName);
          }
          case BusinessMetricsFormInput.AnnualJobs:
          case BusinessMetricsFormInput.AnnualRevenue: {
            return (convertMonetaryToNumber(value?.toString()) || 0) > ANNUAL_REVENUE_AND_UNITS_SOLD_MAX_VALUE
              ? 'The entered value exceeds the maximum allowed limit.'
              : '';
          }
        }
      }

      return '';
    },
  });

  useEffect(() => {
    if (
      values[BusinessMetricsFormInput.PrimaryHomeImprovementCategory] === PrimaryHomeImprovementCategory.Other ||
      values[BusinessMetricsFormInput.OtherHomeImprovementCategory].includes(PrimaryHomeImprovementCategory.Other)
    ) {
      setIsSpecifyHomeImprovementTypeNeeded(true);

      resetForm({
        ...defaultValues,
        ...values,
        [BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType]:
          prequalificationInformation?.specifyHomeImprovementType,
      });
    } else {
      setIsSpecifyHomeImprovementTypeNeeded(false);

      const { [BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType]: deleted, ...restProps } = values;

      resetForm({ ...defaultValues, ...restProps });
    }
  }, [
    values[BusinessMetricsFormInput.PrimaryHomeImprovementCategory],
    values[BusinessMetricsFormInput.OtherHomeImprovementCategory],
  ]);

  useEffect(() => {
    if (values[BusinessMetricsFormInput.DoYouInstallTheProductsYourself] === DefaultYesNoQuestion.No) {
      setIsHowManyInstallersDoYoHaveNeeded(true);

      resetForm({ ...defaultValues, ...values, [BusinessMetricsFormInput.HowManyInstallersDoYouHave]: '' });
    } else {
      setIsHowManyInstallersDoYoHaveNeeded(false);

      const { [BusinessMetricsFormInput.HowManyInstallersDoYouHave]: deleted, ...restProps } = values;

      resetForm({ ...defaultValues, ...restProps });
    }
  }, [values[BusinessMetricsFormInput.DoYouInstallTheProductsYourself]]);

  useEffect(() => {
    if (values[BusinessMetricsFormInput.HoldAppropriateLicense] === DefaultYesNoQuestion.No) {
      setIsExplainSpecificLicenseNeeded(true);

      resetForm({
        ...defaultValues,
        ...values,
        [BusinessMetricsFormInput.ExplainSpecificLicense]: prequalificationInformation?.whyNotHoldLicense,
      });
    } else {
      setIsExplainSpecificLicenseNeeded(false);

      const { [BusinessMetricsFormInput.ExplainSpecificLicense]: deleted, ...restProps } = values;

      resetForm({ ...defaultValues, ...restProps });
    }
  }, [values[BusinessMetricsFormInput.HoldAppropriateLicense]]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;

      setValue(name, value);
    },
    [setValue],
  );

  const handleBlur = useCallback(
    (event: any): void => {
      const name = event.target.name as keyof typeof values;

      validateField(name, values[name]);
    },
    [validateField, values],
  );

  const handleSaveInformation = handleSubmit(async () => {
    try {
      await updatePrequalificationInformation(intermediaryApplicationId!, {
        primaryHomeImprovementCategory: values[BusinessMetricsFormInput.PrimaryHomeImprovementCategory],
        otherHomeImprovementCategory: values[BusinessMetricsFormInput.OtherHomeImprovementCategory],
        holdAppropriateLicense: convertToBooleanFromDefaultQuestionValue(
          values[BusinessMetricsFormInput.HoldAppropriateLicense],
        ),
        whyNotHoldLicense: values[BusinessMetricsFormInput.ExplainSpecificLicense],
        installTheProductsYourself: convertToBooleanFromDefaultQuestionValue(
          values[BusinessMetricsFormInput.DoYouInstallTheProductsYourself],
        ),
        installersAmount: +values[BusinessMetricsFormInput.HowManyInstallersDoYouHave] || null,
        specifyHomeImprovementType: values[BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType] || null,
        percentOfSalesFinanced: +values[BusinessMetricsFormInput.PercentOfSalesFinanced] / 100 || null,
        annualJobsUnitsSold: +values[BusinessMetricsFormInput.AnnualJobs] || null,
        annualRevenue: convertMonetaryToNumber(values[BusinessMetricsFormInput.AnnualRevenue]?.toString()) || null,
        howDidYouHearAboutUs: values[BusinessMetricsFormInput.HowDidYouHearAboutHfs],
        stepType: PrequalificationStep.BusinessMetrics,
      });

      setNextStepByCurrentStep(PrequalificationStep.BusinessMetrics);
    } catch (err) {
      notify(ToastMessage.DefaultError, 'error');
    }
  });

  return (
    <div className={styles.infoContainer}>
      <div className={styles.mainHeader}>Business Metrics</div>
      <div className={styles.formInput}>
        <InputSelect
          useRequiredSymbol
          items={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS}
          currentValue={values[BusinessMetricsFormInput.PrimaryHomeImprovementCategory]}
          onChange={(value) => {
            resetForm({
              ...values,
              [BusinessMetricsFormInput.PrimaryHomeImprovementCategory]: value,
              [BusinessMetricsFormInput.OtherHomeImprovementCategory]: [],
            });
          }}
          label={BusinessMetricsFormInput.PrimaryHomeImprovementCategory}
          errorMessage={errors[BusinessMetricsFormInput.PrimaryHomeImprovementCategory]}
        />
        <MultipleSelect
          id="other_home_improvement_category"
          selectOptions={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.filter(
            (option) => option.label !== values[BusinessMetricsFormInput.PrimaryHomeImprovementCategory],
          )}
          selectAllLabel="All categories"
          useSelectAll
          disabled={false}
          errorMessage={errors[BusinessMetricsFormInput.OtherHomeImprovementCategory]}
          inputLabel={BusinessMetricsFormInput.OtherHomeImprovementCategory}
          initialValues={
            values[BusinessMetricsFormInput.OtherHomeImprovementCategory].map((category: string) =>
              PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.find((option) => option.value === category),
            ) as MultipleSelectOption[]
          }
          onChange={(options) => {
            setValue(
              BusinessMetricsFormInput.OtherHomeImprovementCategory,
              options.map((option) => option.value),
            );
          }}
        />
      </div>
      {isSpecifyHomeImprovementTypeNeeded && (
        <div className={styles.formInput}>
          <TextInput
            useRequiredSymbol
            className={styles.input}
            containerClassName={styles.inputContainer}
            labelTitle={BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType}
            name={BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType}
            value={values[BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType]}
            errorMessage={errors[BusinessMetricsFormInput.PleaseSpecifyHomeImprovementType]}
            onChange={handleChange}
          />
        </div>
      )}
      <div className={styles.formInput}>
        <InputSelect
          useRequiredSymbol
          items={DEFAULT_YES_NO_QUESTION_OPTIONS}
          currentValue={values[BusinessMetricsFormInput.DoYouInstallTheProductsYourself]}
          onChange={(value) => setValue(BusinessMetricsFormInput.DoYouInstallTheProductsYourself, value)}
          label={BusinessMetricsFormInput.DoYouInstallTheProductsYourself}
          errorMessage={errors[BusinessMetricsFormInput.DoYouInstallTheProductsYourself]}
        />
        <InputSelect
          useRequiredSymbol
          items={DEFAULT_YES_NO_QUESTION_OPTIONS}
          currentValue={values[BusinessMetricsFormInput.HoldAppropriateLicense]}
          onChange={(value) => setValue(BusinessMetricsFormInput.HoldAppropriateLicense, value)}
          label={BusinessMetricsFormInput.HoldAppropriateLicense}
          errorMessage={errors[BusinessMetricsFormInput.HoldAppropriateLicense]}
        />
      </div>
      {isHowManyInstallersDoYoHaveNeeded && (
        <div className={styles.formInput}>
          <NumberInput
            useRequiredSymbol
            label={BusinessMetricsFormInput.HowManyInstallersDoYouHave}
            name={BusinessMetricsFormInput.HowManyInstallersDoYouHave}
            value={removeNonDigits(values[BusinessMetricsFormInput.HowManyInstallersDoYouHave])}
            errorMessage={errors[BusinessMetricsFormInput.HowManyInstallersDoYouHave]}
            className={styles.input}
            onChange={handleChange}
            allowNegative={false}
            pattern="\d*"
          />
        </div>
      )}
      {isExplainSpecificLicenseNeeded && (
        <div className={styles.formInput}>
          <TextInput
            useRequiredSymbol
            labelTitle={BusinessMetricsFormInput.ExplainSpecificLicense}
            name={BusinessMetricsFormInput.ExplainSpecificLicense}
            value={values[BusinessMetricsFormInput.ExplainSpecificLicense]}
            errorMessage={errors[BusinessMetricsFormInput.ExplainSpecificLicense]}
            className={styles.input}
            onChange={handleChange}
          />
        </div>
      )}
      <div className={styles.formInput}>
        <NumberInput
          useRequiredSymbol
          label={BusinessMetricsFormInput.AnnualJobs}
          name={BusinessMetricsFormInput.AnnualJobs}
          value={values[BusinessMetricsFormInput.AnnualJobs]}
          errorMessage={errors[BusinessMetricsFormInput.AnnualJobs]}
          className={styles.input}
          onChange={handleChange}
          allowNegative={false}
          pattern="\d*"
        />
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessMetricsFormInput.PercentOfSalesFinanced}
          name={BusinessMetricsFormInput.PercentOfSalesFinanced}
          placeholder="%"
          value={removeNonDigits(values[BusinessMetricsFormInput.PercentOfSalesFinanced])}
          errorMessage={errors[BusinessMetricsFormInput.PercentOfSalesFinanced]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      <div className={styles.formInput}>
        <NumberInput
          useRequiredSymbol
          label={BusinessMetricsFormInput.AnnualRevenue}
          name={BusinessMetricsFormInput.AnnualRevenue}
          value={values[BusinessMetricsFormInput.AnnualRevenue]}
          errorMessage={errors[BusinessMetricsFormInput.AnnualRevenue]}
          className={styles.input}
          prefix="$"
          thousandSeparator
          onChange={handleChange}
          allowNegative={false}
          pattern="\d*"
        />
        <TextInput
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessMetricsFormInput.HowDidYouHearAboutHfs}
          name={BusinessMetricsFormInput.HowDidYouHearAboutHfs}
          value={values[BusinessMetricsFormInput.HowDidYouHearAboutHfs]}
          errorMessage={errors[BusinessMetricsFormInput.HowDidYouHearAboutHfs]}
          onChange={handleChange}
        />
      </div>
      <div className={styles.formInput}>
        <Button onClick={handleSaveInformation} className={styles.saveButton}>
          Save and Continue
        </Button>
      </div>
    </div>
  );
};

export default observer(BusinessMetrics);
